import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import {
    GetCompanyController,
    GetReportController,
    GetScenariosController,
    PopulateReportController
} from "@arch/presentation/controllers";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import { ReportHeaderComponent } from "@app/views/auth/report/components/report-header/report-header.component";
import { ReportBeachmarkingSectionComponent } from "@app/views/auth/report/components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportToolbarComponent } from "@app/views/auth/report/components/report-toolbar/report-toolbar.component";
import { ReportVisualizationComponent } from "@app/views/auth/report/components/report-visualization/report-visualization.component";
import { ReportTableValuesPdfComponent } from "@app/views/auth/report/components/report-table-values-pdf/report-table-values-pdf.component";
import { AboutVisualizationComponent } from "@app/views/auth/report/components/report-visualization-explore/about-visualization/about-visualization.component";
import { ReportPdfFooterComponent } from "@app/views/auth/report/components/report-pdf-footer/report-pdf-footer.component";
import { ReportPdfIndexComponent } from "@app/views/auth/report/components/report-pdf-index/report-pdf-index.component";
import { ReportVisualizationNoteComponent } from "@app/views/auth/report/components/report-visualization-note/report-visualization-note.component";
import { ReportPerformanceComponent } from "@app/views/auth/report/components/report-performance/report-performance.component";
import { TableBalanceComponent } from "@app/shared/components/table-balance/table-balance.component";
import { PdfService } from "@app/shared/services/pdf.service";
import { ReportPreferenceService } from "@arch/domain/application/services";

@Component({
    selector: "app-pdf-balance",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ReportHeaderComponent,
        ReportBeachmarkingSectionComponent,
        ReportToolbarComponent,
        ReportVisualizationComponent,
        ReportTableValuesPdfComponent,
        AboutVisualizationComponent,
        ReportPdfFooterComponent,
        ReportPdfIndexComponent,
        ReportVisualizationNoteComponent,
        ReportPerformanceComponent,
        TableBalanceComponent
    ],
    templateUrl: "./pdf.component.html",
    styles: [
        `
            .divider {
                border-bottom: var(--border-width-1) solid
                    var(--color-border-light);
            }
            .note-container {
                padding: var(--spacing-8);
                background-color: var(--color-expand-collapse-notes-bg);
                border-radius: var(--border-radius-8);
                gap: var(--spacing-8);
                .fa-sticky-note {
                    font-size: var(--font-size-12);
                }
            }
            .pdf-section-about-visualization {
                margin-top: var(--spacing-8);
            }
        `
    ],
    styleUrls: ["../../pdf-config.scss"]
})
export class PdfBalanceComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly populateReportController = inject(
        PopulateReportController
    );
    public pdfService = inject(PdfService);
    public reportPreferenceService = inject(ReportPreferenceService);
    public report: ReportEntity;
    public company: CompanyEntity;
    public loading: boolean = true;
    public hasBenchmarking: boolean = true;
    public tableBalancePages: { assets: number[]; liabilities: number[] }[] = [
        {
            assets: Array.from({ length: 13 }, (_, i) => i),
            liabilities: Array.from({ length: 13 }, (_, i) => i)
        },
        {
            assets: Array.from({ length: 10 }, (_, i) => i + 13),
            liabilities: Array.from({ length: 10 }, (_, i) => i + 13)
        }
    ];

    async ngOnInit(): Promise<void> {
        this.loading = true;
        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        this.reportPreferenceService.setAll(report.props?.optionsContent);
        if (report.options.comparation.active) {
            this.tableBalancePages = [
                {
                    assets: Array.from({ length: 7 }, (_, i) => i),
                    liabilities: Array.from({ length: 7 }, (_, i) => i)
                },
                {
                    assets: Array.from({ length: 8 }, (_, i) => i + 7),
                    liabilities: Array.from({ length: 8 }, (_, i) => i + 7)
                },
                {
                    assets: Array.from({ length: 8 }, (_, i) => i + 16),
                    liabilities: Array.from({ length: 8 }, (_, i) => i + 16)
                }
            ];
        }
        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: report.props.isGroup ? false : true
                }
            })
        ]);
        report.setScenarioOptions(scenarios);
        this.company = company;
        this.report = report;
        this.pdfService.config(this.report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                balance: true,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
        this.pdfService.print();
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
    }
}
